import type { User } from '@supabase/supabase-js';

/**
 * Default landing route for a signed-in user, based on their role claim.
 *
 * Staff (`staff`, `admin`, `super_admin`) land in the admin console (`/admin`).
 * Customers land in the client portal (`/app`).
 *
 * Used by:
 *   - SignInPage (post-login navigation)
 *   - ProtectedRoute (rejects staff from `/app`)
 *   - StaffRoute (rejects non-staff from `/admin`)
 *   - App.tsx root + catch-all redirects
 */

const STAFF_ROLES = new Set(['staff', 'admin', 'super_admin']);

/** Full internal staff — the whole admin console. Excludes `support`. */
export function isStaffUser(user: User | null | undefined): boolean {
  if (!user) return false;
  const role = (user.app_metadata as { role?: string } | undefined)?.role;
  return !!role && STAFF_ROLES.has(role);
}

/** Support agent — scoped to the support inbox only (2026-07-06). */
export function isSupportUser(user: User | null | undefined): boolean {
  if (!user) return false;
  return (user.app_metadata as { role?: string } | undefined)?.role === 'support';
}

/** Any internal user with admin-console access (full staff OR support). */
export function isInternalUser(user: User | null | undefined): boolean {
  return isStaffUser(user) || isSupportUser(user);
}

export function defaultRouteForUser(user: User | null | undefined): string {
  if (isStaffUser(user)) return '/admin';
  // Support lands on Customers (operational), not the financial dashboard.
  if (isSupportUser(user)) return '/admin/customers';
  return '/app';
}

/**
 * Landing route accounting for Phase 11 EXTERNAL sales staff.
 *
 * INCIDENT 2026-08-03: `defaultRouteForUser` can only see `auth.users`
 * app_metadata. Per SP-18, external sales staff deliberately carry NO
 * app_metadata.role — they are identified solely by `public.staff_profile`.
 * So for a pure sales agent every role predicate above returns false and the
 * function falls through to `/app`, landing them in the CUSTOMER PORTAL.
 *
 * This stayed hidden because every agent until now ALSO held the `support` auth
 * role, which bounced them to /admin/customers and masked the fall-through. The
 * first agent onboarded WITHOUT support hit it on their first sign-in.
 *
 * (Names and the partner company are deliberately omitted here: this file ships
 * to browsers and vite emits public source maps, so comments are fetchable from
 * the live site. Detail is in the audit_log and the incident commit message.)
 *
 * `isSalesStaff` must come from `useStaffRole()`, which reads staff_profile.
 * Callers MUST wait for that hook's `loading` to settle first, or a sales agent
 * is briefly routed as a customer.
 *
 * Every guard that decides "where does this user belong" uses THIS function —
 * HomeRedirect, ProtectedRoute and SignInPage — so the rule cannot drift again.
 */
export function defaultRouteForRole(
  user: User | null | undefined,
  isSalesStaff: boolean
): string {
  if (isStaffUser(user)) return '/admin';
  if (isSupportUser(user)) return '/admin/customers';
  if (isSalesStaff) return '/sales';
  return '/app';
}
